import { useResumableString } from '../../lib/session-resume';
import { useEffect, useState } from 'react';
import type { ActiveSession, OemContext } from '../../lib/auth';
import { getActiveSession, adminSetupRetirement } from '../../lib/auth';
import { request, contextInventory } from '../../lib/oem-client';
import { pinRequest } from '../../lib/staff-pin';
import { customerRecoveryCapability } from '../../lib/customer-recovery';
import { StaffPinManagement } from './staff-pin-management';
import { CustomerAccountForm } from './customer-account-form';
import { roleLabel, actionLabel } from './display-copy';
interface Project {
  id: string;
  label: string;
  customer: string;
}
interface Member {
  userId: string;
  name: string;
  active: boolean;
  pinEnabled: boolean;
  role: string;
  validUntil: string | null;
  projects: {
    authorityId: string;
    id: string;
    label: string;
    role: string;
    actions: string[];
    active: boolean;
    validUntil: string;
  }[];
}
interface Customer {
  userId: string;
  username: string;
  name: string;
  active: boolean;
  accepted: boolean;
  projectId: string;
  projectLabel: string;
}
interface Centre {
  organization: { id: string; label: string };
  members: Member[];
  projects: Project[];
  customers: Customer[];
}
export function AdminCentre({ session }: { session: ActiveSession }) {
  const orgs = session.organizationContexts.filter(
    (c) => c.role === 'admin' && c.actions.includes('manage_staff'),
  );
  const [org, setOrg] = useState(orgs[0]?.organizationId ?? '');
  const [data, setData] = useState<Centre | null>(null),
    [error, setError] = useState('');
  const [project, setProject] = useState(''),
    [user, setUser] = useState(''),
    [role, setRole] = useState('rd'),
    [evidence, setEvidence] = useState(''),
    [expires, setExpires] = useState(''),
    [busy, setBusy] = useState(false);
  const [customer, setCustomer] = useState(''),
    [method, setMethod] = useState('in_person'),
    [attested, setAttested] = useState(false),
    [link, setLink] = useState('');
  const load = () => request('/admin-centre/' + org).then((x) => setData(x as Centre));
  useEffect(() => {
    let live = true;
    if (org)
      void request('/admin-centre/' + org).then(
        (x) => {
          if (live) setData(x as Centre);
        },
        () => {
          if (live) setError('โหลดศูนย์จัดการไม่ได้ ตรวจสอบสิทธิ์และการเชื่อมต่อ');
        },
      );
    return () => {
      live = false;
    };
  }, [org]);
  if (!orgs.length)
    return (
      <section className="card">
        <h1>ศูนย์จัดการบัญชี</h1>
        <p>ต้องมีสิทธิ์ผู้ดูแลองค์กร</p>
      </section>
    );
  async function run(work: () => Promise<unknown>) {
    setBusy(true);
    setError('');
    try {
      await work();
      await load();
    } catch {
      setError('ดำเนินการไม่ได้ ตรวจสอบสิทธิ์ หลักฐาน และวันหมดอายุ');
    } finally {
      setBusy(false);
    }
  }
  const selected = data?.customers.find((c) => c.userId === customer);
  return (
    <>
      <h1>ศูนย์จัดการบัญชีและโครงการ</h1>
      <label>
        องค์กร
        <select
          value={org}
          onChange={(e) => {
            setData(null);
            setLink('');
            setOrg(e.target.value);
          }}
        >
          {orgs.map((c) => (
            <option key={c.organizationId} value={c.organizationId}>
              {data?.organization.id === c.organizationId
                ? data.organization.label
                : (c.label ?? 'องค์กรของคุณ')}
            </option>
          ))}
        </select>
      </label>
      {error ? <p role="alert">{error}</p> : null}
      {!data ? (
        <p role="status">กำลังตรวจสอบข้อมูล…</p>
      ) : (
        <>
          <section className="card">
            <h2>{data.organization.label}</h2>
            <a className="action" href="/app/setup">
              เริ่มลูกค้าและโครงการใหม่
            </a>
            {!data.projects.length ? (
              <p>ยังไม่มีโครงการ เริ่มจากลูกค้าและคำขอสินค้า แล้วเลือกทีมงานที่รับผิดชอบ</p>
            ) : null}
            {data.members.map((m) => (
              <article className="card" key={m.userId}>
                <h3>
                  {m.name} · {roleLabel(m.role)}
                </h3>
                <p>
                  {m.active ? 'บัญชีใช้งาน' : 'บัญชีปิด'} · {m.pinEnabled ? 'PIN เปิด' : 'PIN ปิด'}
                  {m.validUntil
                    ? ` · สิ้นสุด ${new Date(m.validUntil).toLocaleString('th-TH')}`
                    : ''}
                </p>
                {m.projects.length ? (
                  m.projects.map((p) => (
                    <p key={p.id}>
                      {p.label} · {roleLabel(p.role)} ·{' '}
                      {p.active ? 'ใช้งาน' : 'สิทธิ์หมดอายุ/เพิกถอน'} ·{' '}
                      {p.actions.map(actionLabel).join(' / ')}
                    </p>
                  ))
                ) : (
                  <p>ยังไม่ได้รับสิทธิ์โครงการ</p>
                )}
              </article>
            ))}
          </section>
          <section className="card">
            <h2>กำหนดทีมงานในโครงการ</h2>
            <p>เลือกคนและสิทธิ์อย่างชัดเจน การเป็นสมาชิกทีมไม่ได้ให้สิทธิ์โครงการอัตโนมัติ</p>
            <form
              className="form-grid"
              onSubmit={(e) => {
                e.preventDefault();
                void run(() =>
                  request('/staff', {
                    projectId: project,
                    userId: user,
                    role,
                    evidence,
                    expiresAt: new Date(expires).toISOString(),
                  }),
                );
              }}
            >
              <label>
                โครงการ
                <select required value={project} onChange={(e) => setProject(e.target.value)}>
                  <option value="">เลือกโครงการ</option>
                  {data.projects.map((p) => (
                    <option value={p.id} key={p.id}>
                      {p.customer} · {p.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                ทีมงาน
                <select required value={user} onChange={(e) => setUser(e.target.value)}>
                  <option value="">เลือกคน</option>
                  {data.members
                    .filter((m) => m.active && m.userId !== session.userId)
                    .map((m) => (
                      <option key={m.userId} value={m.userId}>
                        {m.name}
                      </option>
                    ))}
                </select>
              </label>
              <label>
                หน้าที่
                <select value={role} onChange={(e) => setRole(e.target.value)}>
                  {[
                    'coordinator',
                    'rd',
                    'rd_checker',
                    'qa',
                    'owner',
                    'finance',
                    'purchasing',
                    'designer',
                    'production',
                    'auditor',
                    'admin',
                  ].map((r) => (
                    <option key={r} value={r}>
                      {roleLabel(r)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                หลักฐาน
                <textarea
                  required
                  value={evidence}
                  maxLength={4000}
                  onChange={(e) => setEvidence(e.target.value)}
                />
              </label>
              <label>
                สิทธิ์สิ้นสุด
                <input
                  required
                  type="datetime-local"
                  value={expires}
                  onChange={(e) => setExpires(e.target.value)}
                />
              </label>
              <button className="action" disabled={busy || !navigator.onLine}>
                บันทึกสิทธิ์ที่เลือก
              </button>
            </form>
            {data.members.flatMap((m) =>
              m.projects
                .filter((p) => p.active)
                .map((p) => (
                  <div key={p.authorityId}>
                    <span>
                      {m.name} · {p.label} · {roleLabel(p.role)}{' '}
                    </span>
                    <button
                      disabled={busy || !navigator.onLine}
                      onClick={() =>
                        void run(() =>
                          request('/authorities/revoke', {
                            projectId: p.id,
                            authorityId: p.authorityId,
                          }),
                        )
                      }
                    >
                      เพิกถอนสิทธิ์
                    </button>
                  </div>
                )),
            )}
          </section>
          <section className="card">
            <h2>ช่วยลูกค้าตั้งรหัสผ่านใหม่</h2>
            <p>ตรวจสอบตัวบุคคลผ่านช่องทางที่ยืนยันแล้วก่อนออกลิงก์ ผู้รับเลือกรหัสผ่านส่วนตัวเอง</p>
            <label>
              บัญชีลูกค้า
              <select
                value={customer}
                onChange={(e) => {
                  setCustomer(e.target.value);
                  setLink('');
                  setAttested(false);
                }}
              >
                <option value="">เลือกบัญชีที่รับสิทธิ์แล้ว</option>
                {data.customers
                  .filter((c) => c.active && c.accepted)
                  .map((c) => (
                    <option key={c.userId} value={c.userId}>
                      {c.name} · {c.username} · {c.projectLabel}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              วิธียืนยัน
              <select value={method} onChange={(e) => setMethod(e.target.value)}>
                <option value="in_person">พบเจ้าตัว</option>
                <option value="verified_private_contact">ช่องทางส่วนตัวที่ตรวจสอบผู้รับแล้ว</option>
              </select>
            </label>
            <label>
              หลักฐานการยืนยัน
              <textarea
                value={evidence}
                minLength={10}
                maxLength={4000}
                onChange={(e) => setEvidence(e.target.value)}
              />
            </label>
            <label>
              <input
                type="checkbox"
                checked={attested}
                onChange={(e) => setAttested(e.target.checked)}
              />
              ฉันตรวจสอบว่าเป็นเจ้าของบัญชีและผู้รับลิงก์ตัวจริงแล้ว
            </label>
            <button
              disabled={
                busy || !selected || !attested || evidence.trim().length < 10 || !navigator.onLine
              }
              onClick={() =>
                void run(async () => {
                  if (!selected) return;
                  const r = (await request('/customer-recovery', {
                    organizationId: org,
                    projectId: selected.projectId,
                    userId: selected.userId,
                    username: selected.username,
                    method,
                    evidence,
                    attested,
                  })) as { link: string };
                  setLink(r.link);
                })
              }
            >
              ออกลิงก์ส่วนตัวครั้งเดียว
            </button>
            {link ? (
              <div className="note-warning">
                <p>ส่งลิงก์นี้ให้เจ้าของบัญชีผ่านช่องทางส่วนตัวภายใน 20 นาที · ลิงก์เป็นความลับ</p>
                <input
                  aria-label="ลิงก์ตั้งรหัสผ่านส่วนตัว"
                  readOnly
                  value={link}
                  onFocus={(e) => e.target.select()}
                />
                <button onClick={() => setLink('')}>ซ่อนลิงก์</button>
              </div>
            ) : null}
          </section>
        </>
      )}
      <StaffPinManagement session={session} />
    </>
  );
}
interface Task {
  id: string;
  rootId: string;
  projectId: string;
  projectLabel: string;
  audience: 'staff' | 'customer';
  label: string;
  state: string;
  dueAt: string | null;
  kind: string;
  nextAction: string;
}
export function MyWork() {
  const [items, setItems] = useState<Task[]>([]),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(true),
    [more, setMore] = useState(false);
  async function load(after?: string) {
    try {
      const r = (await request(
        '/my-work?limit=30' + (after ? '&after=' + encodeURIComponent(after) : ''),
      )) as Task[];
      setItems((old) => (after ? [...old, ...r] : r));
      setMore(r.length === 30);
    } catch {
      setError('โหลดงานไม่ได้ กรุณาตรวจสอบสิทธิ์หรือเข้าสู่ระบบอีกครั้ง');
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    let live = true;
    void request('/my-work?limit=30')
      .then(
        (value) => {
          if (live) {
            const rows = value as Task[];
            setItems(rows);
            setMore(rows.length === 30);
          }
        },
        () => {
          if (live) setError('โหลดงานไม่ได้ กรุณาตรวจสอบสิทธิ์หรือเข้าสู่ระบบอีกครั้ง');
        },
      )
      .finally(() => {
        if (live) setBusy(false);
      });
    return () => {
      live = false;
    };
  }, []);
  return (
    <section className="card">
      <h1>งานของฉัน · ทุกโครงการ</h1>
      <p>เฉพาะงานฉบับล่าสุดในโครงการที่คุณยังมีสิทธิ์</p>
      {busy ? <p role="status">กำลังโหลดงาน…</p> : null}
      {error ? (
        <p role="alert">{error}</p>
      ) : !busy && !items.length ? (
        <p>ยังไม่มีงานในโครงการที่คุณได้รับสิทธิ์</p>
      ) : null}
      {items.map((t) => (
        <a
          className="queue-item"
          key={t.id}
          href={`${t.audience === 'customer' ? '/portal' : '/app'}/projects/${t.projectId}/work-list`}
        >
          <strong>{t.label}</strong>
          <span>
            {t.projectLabel} · {t.state} · {t.dueAt ?? 'ยังไม่กำหนดวันส่ง'}
          </span>
        </a>
      ))}
      {more ? (
        <button disabled={busy} onClick={() => void load(items.at(-1)?.id)}>
          โหลดงานเพิ่มเติม
        </button>
      ) : null}
      <ReminderInbox />
    </section>
  );
}
interface Inbox {
  preferences: { inApp: boolean; lineOptIn: boolean; lineLinked: boolean };
  lineConfigured: boolean;
  lineAccountUrl: string | null;
  items: { id: string; projectId: string; rule: string; deadline: string; state: string }[];
}
export function ReminderInbox() {
  const [data, setData] = useState<Inbox | null>(null),
    [message, setMessage] = useState(''),
    [code, setCode] = useState('');
  const load = () => request('/reminders').then((x) => setData(x as Inbox));
  useEffect(() => {
    void load().catch(() => setMessage('โหลดการแจ้งเตือนไม่ได้'));
  }, []);
  async function preferences(inApp: boolean, line: boolean) {
    try {
      await request('/reminders/preferences', { inApp, line });
      await load();
    } catch {
      setMessage('เปลี่ยนการแจ้งเตือนไม่ได้');
    }
  }
  return (
    <section className="card">
      <h2>การแจ้งเตือนของฉัน</h2>
      {message ? <p role="status">{message}</p> : null}
      {data ? (
        <>
          <label>
            <input
              type="checkbox"
              checked={data.preferences.inApp}
              onChange={(e) => void preferences(e.target.checked, data.preferences.lineOptIn)}
            />
            แจ้งเตือนในแอป
          </label>
          <p>ตรวจงานที่ใกล้ครบกำหนดภายใน 24 ชั่วโมง และสรุปงานเกินกำหนดวันละครั้ง · เวลากรุงเทพฯ</p>
          {!data.lineConfigured ? (
            <p>LINE ยังไม่เชื่อมต่อ ผู้ดูแลต้องตั้งค่า SQI Official Account ก่อนเปิดใช้งาน</p>
          ) : (
            <>
              <a href={data.lineAccountUrl ?? undefined} target="_blank" rel="noreferrer">
                เปิด SQI Official Account
              </a>
              {!data.preferences.lineLinked ? (
                <button
                  onClick={() =>
                    void getActiveSession()
                      .then(async (s) => {
                        if (!s) return;
                        const r = (await request('/reminders/line-link', {
                          organizationId: s.contexts[0]?.organizationId,
                        })) as { code: string };
                        setCode(r.code);
                      })
                      .catch(() => setMessage('สร้างรหัสเชื่อมต่อไม่ได้'))
                  }
                >
                  สร้างรหัสเชื่อมต่อ LINE ส่วนตัว
                </button>
              ) : null}
              {code ? (
                <>
                  <p>ส่งข้อความนี้ในแชตส่วนตัวกับ SQI ภายใน 10 นาที จากนั้นโหลดสถานะใหม่</p>
                  <input aria-label="รหัสเชื่อมต่อ LINE" readOnly value={'SQI-LINK ' + code} />
                  <button
                    onClick={() => {
                      setCode('');
                      void load();
                    }}
                  >
                    ตรวจสถานะเชื่อมต่อ
                  </button>
                </>
              ) : null}
              <label>
                <input
                  type="checkbox"
                  disabled={!data.preferences.lineLinked}
                  checked={data.preferences.lineOptIn}
                  onChange={(e) => void preferences(data.preferences.inApp, e.target.checked)}
                />
                ยินยอมรับ LINE · ส่งเพียงจำนวนงานและลิงก์เข้าแอป
              </label>
            </>
          )}
          {data.items.map((i) => (
            <p key={i.id}>
              {i.rule === 'overdue' ? 'งานเลยกำหนด' : 'งานใกล้ครบกำหนด'} ·{' '}
              {new Date(i.deadline).toLocaleString('th-TH')} ·{' '}
              {i.state === 'accepted'
                ? 'LINE รับคำขอแล้ว · ไม่ใช่หลักฐานว่าอ่านแล้ว'
                : 'แจ้งเตือนในแอป'}
            </p>
          ))}
        </>
      ) : (
        <p role="status">กำลังโหลดการแจ้งเตือน…</p>
      )}
    </section>
  );
}
export function CustomerRecovery() {
  const [password, setPassword] = useState(''),
    [confirmation, setConfirmation] = useState(''),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState(''),
    [done, setDone] = useState(false);
  return (
    <main className="frame login-frame">
      <section className="card">
        <h1>ตั้งรหัสผ่านส่วนตัวใหม่</h1>
        <p>ใช้ลิงก์ที่ผู้ดูแลออกหลังตรวจสอบตัวบุคคล คุณเป็นผู้เลือกรหัสผ่านเอง</p>
        {!customerRecoveryCapability ? (
          <p role="alert">ลิงก์ไม่พร้อมใช้งาน กรุณาขอลิงก์ใหม่จากผู้ดูแล</p>
        ) : done ? (
          <a className="action" href="/login">
            เข้าสู่ระบบด้วยรหัสผ่านใหม่
          </a>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (password !== confirmation) return;
              setBusy(true);
              void adminSetupRetirement
                .then((ok) => {
                  if (!ok) throw Error('unavailable');
                  return pinRequest('/customer-recovery', {
                    capability: customerRecoveryCapability,
                    password,
                  });
                })
                .then(
                  () => {
                    setDone(true);
                    setMessage('เปลี่ยนรหัสผ่านและยกเลิกเซสชันเดิมแล้ว');
                  },
                  () =>
                    setMessage(
                      'ดำเนินการไม่ได้ ลิงก์อาจหมดอายุ หรือให้ลองคำขอเดิมอีกครั้ง หากยังไม่ได้ให้ติดต่อผู้ดูแล',
                    ),
                )
                .finally(() => {
                  setPassword('');
                  setConfirmation('');
                  setBusy(false);
                });
            }}
          >
            <label>
              รหัสผ่านใหม่
              <input
                required
                type="password"
                autoComplete="new-password"
                minLength={12}
                maxLength={72}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            <label>
              ยืนยันรหัสผ่าน
              <input
                required
                type="password"
                autoComplete="new-password"
                minLength={12}
                maxLength={72}
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
              />
            </label>
            {password && confirmation && password !== confirmation ? (
              <p role="alert">รหัสผ่านไม่ตรงกัน</p>
            ) : null}
            <button
              className="action"
              disabled={busy || !navigator.onLine || password !== confirmation}
            >
              บันทึกรหัสผ่านส่วนตัว
            </button>
          </form>
        )}
        {message ? <p role="status">{message}</p> : null}
      </section>
    </main>
  );
}
interface Journey {
  id: string;
  step: number;
  receipts: Record<
    string,
    {
      id?: string;
      skuIds?: string[];
      receiptId?: string;
      skipped?: boolean;
      summary?: string;
      name?: string;
      role?: string;
    }
  >;
}
export function FirstProjectSetup({ session }: { session: ActiveSession }) {
  const orgs = session.organizationContexts.filter((c) => c.actions.includes('create_project'));
  const [org, setOrg] = useState(orgs[0]?.organizationId ?? ''),
    [journey, setJourney] = useState<Journey | null>(null),
    [inventory, setInventory] = useState<Awaited<ReturnType<typeof contextInventory>> | null>(null),
    [centre, setCentre] = useState<Centre | null>(null);
  const recoveryForm = 'first-project:' + session.userId + ':' + org;
  const [customer, setCustomer] = useResumableString(recoveryForm, 'customerId'),
    [customerName, setCustomerName] = useResumableString(recoveryForm, 'customerName'),
    [brand, setBrand] = useResumableString(recoveryForm, 'brand'),
    [label, setLabel] = useResumableString(recoveryForm, 'label'),
    [brief, setBrief] = useResumableString(recoveryForm, 'brief'),
    [skus, setSkus] = useResumableString(recoveryForm, 'skuLabels'),
    [user, setUser] = useState(''),
    [role, setRole] = useState('rd'),
    [evidence, setEvidence] = useState(''),
    [expires, setExpires] = useState(''),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState(''),
    [view, setView] = useState<number | null>(null),
    [projectContext, setProjectContext] = useState<OemContext | null>(null);

  useEffect(() => {
    let live = true;
    if (org) {
      void request('/setup-journey', { organizationId: org, step: 'read' }).then(
        (j) => {
          if (live) setJourney(j as Journey);
        },
        () => {
          if (live) setMessage('โหลดขั้นตอนที่บันทึกไม่ได้');
        },
      );
      void contextInventory().then((x) => {
        if (live) setInventory(x);
      });
      void request('/admin-centre/' + org).then(
        (x) => {
          if (live) setCentre(x as Centre);
        },
        () => {
          if (live) setMessage('อ่านรายชื่อทีมงานไม่ได้ · ข้ามการกำหนดทีมได้');
        },
      );
    }
    return () => {
      live = false;
    };
  }, [org]);
  useEffect(() => {
    let live = true;
    const projectId = journey?.receipts.project?.id;
    if (projectId)
      void getActiveSession().then((s) => {
        if (live) setProjectContext(s?.contexts.find((c) => c.projectId === projectId) ?? null);
      });
    return () => {
      live = false;
    };
  }, [journey]);
  const step = view ?? journey?.step ?? 0;
  async function advance(name: string, payload: Record<string, unknown>) {
    setBusy(true);
    setMessage('');
    try {
      setJourney(
        (await request('/setup-journey', { organizationId: org, step: name, payload })) as Journey,
      );
      setView(null);
    } catch {
      setMessage('บันทึกไม่ได้ ขั้นตอนที่สำเร็จแล้วจะไม่สร้างซ้ำ กรุณาลองขั้นตอนเดิม');
    } finally {
      setBusy(false);
    }
  }
  if (!orgs.length)
    return (
      <section className="card">
        <h1>เริ่มโครงการ</h1>
        <p>ต้องมีสิทธิ์สร้างโครงการในองค์กร</p>
      </section>
    );
  return (
    <section className="card">
      <h1>เริ่มลูกค้าและโครงการแรก</h1>
      <p>ลูกค้า → แบรนด์ → โครงการและคำขอสินค้า → ทีมงาน → บัญชีลูกค้า</p>
      <label>
        องค์กร
        <select
          value={org}
          onChange={(e) => {
            setOrg(e.target.value);
            setJourney(null);
            setView(null);
          }}
        >
          {orgs.map((o) => (
            <option value={o.organizationId} key={o.organizationId}>
              {o.label ?? 'องค์กรของคุณ'}
            </option>
          ))}
        </select>
      </label>
      <p role="status">
        ขั้นตอน {Math.min(step + 1, 5)} / 5 · บันทึกสำเร็จแล้ว {journey?.step ?? 0} ขั้นตอน
      </p>
      {message ? <p role="alert">{message}</p> : null}
      {!journey ? (
        <p>กำลังโหลดขั้นตอน…</p>
      ) : (
        <>
          {step < journey.step ? (
            <p className="note-warning">
              ขั้นตอนนี้บันทึกแล้ว · ย้อนดูได้โดยไม่สร้างข้อมูลซ้ำ{' '}
              <button onClick={() => setView(null)}>กลับขั้นตอนปัจจุบัน</button>
            </p>
          ) : null}
          {step === 0 ? (
            <>
              <label>
                ลูกค้าที่มีอยู่
                <select value={customer} onChange={(e) => setCustomer(e.target.value)}>
                  <option value="">เพิ่มลูกค้าใหม่</option>
                  {inventory?.customers
                    .filter((c) => c.organizationId === org)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                </select>
              </label>
              {!customer ? (
                <label>
                  ชื่อลูกค้า
                  <input
                    required
                    maxLength={300}
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                  />
                </label>
              ) : null}
              <button
                disabled={busy || (!customer && !customerName.trim()) || !navigator.onLine}
                onClick={() =>
                  void advance(
                    'customer',
                    customer ? { customerId: customer } : { label: customerName },
                  )
                }
              >
                บันทึกลูกค้าและไปต่อ
              </button>
            </>
          ) : null}
          {step === 1 ? (
            <>
              <label>
                แบรนด์ (ข้ามได้)
                <input value={brand} maxLength={300} onChange={(e) => setBrand(e.target.value)} />
              </label>
              <button
                disabled={busy || !navigator.onLine}
                onClick={() => void advance('brand', { label: brand })}
              >
                บันทึกแบรนด์ / ข้าม
              </button>
            </>
          ) : null}
          {step === 2 ? (
            <>
              <label>
                ชื่อโครงการ
                <input value={label} maxLength={300} onChange={(e) => setLabel(e.target.value)} />
              </label>
              <label>
                คำขอสินค้าเริ่มต้น
                <textarea
                  value={brief}
                  maxLength={4000}
                  onChange={(e) => setBrief(e.target.value)}
                />
              </label>
              <p>บันทึกเป็นคำขอเริ่มต้น ผู้มีสิทธิ์เขียนบรีฟต้องตรวจและบันทึกบรีฟทางการอีกครั้ง</p>
              <label>
                SKU · หนึ่งรายการต่อบรรทัด
                <textarea value={skus} onChange={(e) => setSkus(e.target.value)} />
              </label>
              <button
                disabled={
                  busy || !label.trim() || !brief.trim() || !skus.trim() || !navigator.onLine
                }
                onClick={() =>
                  void advance('project', {
                    label,
                    source: brief,
                    skuLabels: skus
                      .split('\n')
                      .map((s) => s.trim())
                      .filter(Boolean),
                  })
                }
              >
                สร้างโครงการและเก็บคำขอ
              </button>
            </>
          ) : null}
          {step === 3 ? (
            <>
              <p>เลือกทีมงานที่มีอยู่ ไม่มีการเพิ่มสิทธิ์อัตโนมัติ</p>
              <label>
                ทีมงาน
                <select value={user} onChange={(e) => setUser(e.target.value)}>
                  <option value="">ยังไม่กำหนด</option>
                  {centre?.members
                    .filter((m) => m.active && m.userId !== session.userId)
                    .map((m) => (
                      <option key={m.userId} value={m.userId}>
                        {m.name}
                      </option>
                    ))}
                </select>
              </label>
              {user ? (
                <>
                  <label>
                    หน้าที่
                    <select value={role} onChange={(e) => setRole(e.target.value)}>
                      {[
                        'coordinator',
                        'rd',
                        'rd_checker',
                        'qa',
                        'production',
                        'owner',
                        'finance',
                        'designer',
                      ].map((r) => (
                        <option value={r} key={r}>
                          {roleLabel(r)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    หลักฐาน
                    <textarea value={evidence} onChange={(e) => setEvidence(e.target.value)} />
                  </label>
                  <label>
                    สิทธิ์สิ้นสุด
                    <input
                      type="datetime-local"
                      value={expires}
                      onChange={(e) => setExpires(e.target.value)}
                    />
                  </label>
                </>
              ) : null}
              <button
                disabled={busy || !navigator.onLine || (!!user && (!evidence.trim() || !expires))}
                onClick={() =>
                  void advance(
                    'team',
                    user
                      ? { userId: user, role, evidence, expiresAt: new Date(expires).toISOString() }
                      : {},
                  )
                }
              >
                บันทึกทีมงาน / ข้าม
              </button>
            </>
          ) : null}
          {step >= 4 ? (
            <>
              <h2>โครงการพร้อมสำหรับขั้นตอนถัดไป</h2>
              <p>
                คำขอสินค้าเริ่มต้นบันทึกแล้ว ·{' '}
                {journey.receipts.formalBrief
                  ? 'บันทึกบรีฟร่างทางการแล้ว'
                  : 'ยังไม่ได้บันทึกหรืออนุมัติบรีฟทางการ'}
              </p>
              <p>ให้ผู้รับผิดชอบโครงการเปิดคำขอ ตรวจรายละเอียด และบันทึกบรีฟตามสิทธิ์</p>
              {journey.receipts.project?.summary ? (
                <p>คำขอที่บันทึก: {journey.receipts.project.summary}</p>
              ) : null}
              {journey.receipts.team?.name ? (
                <p>
                  ผู้รับผิดชอบที่เลือก: {journey.receipts.team.name} ·{' '}
                  {roleLabel(journey.receipts.team.role ?? '')}
                </p>
              ) : (
                <p>ยังไม่ได้ระบุผู้รับผิดชอบ · กำหนดทีมงานในศูนย์จัดการก่อนทำงานต่อ</p>
              )}
              {projectContext?.actions.includes('create') ? (
                <button
                  disabled={Boolean(journey.receipts.formalBrief) || busy}
                  onClick={() => void advance('brief', {})}
                >
                  บันทึกบรีฟร่างตามสิทธิ์ของฉัน
                </button>
              ) : (
                <p>บัญชีนี้ไม่มีสิทธิ์เขียนบรีฟ ให้ทีมงานที่ได้รับมอบหมายบันทึกต่อ</p>
              )}
              {projectContext ? <CustomerAccountForm context={projectContext} /> : null}
              <p>บัญชีลูกค้าเป็นขั้นตอนเพิ่มเติม รหัสผ่านเก็บเฉพาะในแบบฟอร์มนี้</p>
              <a
                className="action"
                href={'/app/projects/' + (journey.receipts.project?.id ?? '') + '/home'}
              >
                เปิดโครงการที่สร้างแล้ว
              </a>
              <button disabled={busy} onClick={() => void advance('finish', {})}>
                จบขั้นตอน · เริ่มใหม่ครั้งถัดไป
              </button>
            </>
          ) : null}
          {step > 0 ? (
            <button onClick={() => setView(Math.max(0, step - 1))}>ย้อนดูขั้นตอนก่อนหน้า</button>
          ) : null}
        </>
      )}
    </section>
  );
}
